import type { ChangeRequest, LocalizedText } from '../model/schema';
import { isLocalizedText, payloadBag, requestBaselineOf } from './entity-bag';
import { firstNonEmpty } from './translate';

/** Prefer uz (admin canonical) → ru → en; never render an empty name. */
export function resolveEntityName(
  name: LocalizedText | null | undefined,
  fallback: string,
): string {
  return firstNonEmpty(name?.uz?.trim(), name?.ru?.trim(), name?.en?.trim()) ?? fallback;
}

/** Entity behind a queue row — absent for a pending create. */
export function requestEntityId(request: Pick<ChangeRequest, 'entity_id'>): string {
  return firstNonEmpty(request.entity_id?.trim()) ?? '—';
}

/**
 * Display name of the request's entity. `entity_name` is filled on the list
 * reads but absent on a pending create (no entity to name yet), so the
 * proposed `payload.name` and then the `baseline.name` stand in.
 */
export function requestEntityName(
  request: Pick<ChangeRequest, 'entity_name' | 'payload' | 'baseline'>,
  fallback: string,
): string {
  const payloadName: unknown = payloadBag(request).name;
  const baselineName: unknown = requestBaselineOf(request)?.name;
  for (const candidate of [request.entity_name, payloadName, baselineName]) {
    if (isLocalizedText(candidate)) {
      const resolved = resolveEntityName(candidate, '');
      if (resolved !== '') return resolved;
    }
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
  }
  return fallback;
}

/** Name of the row's parent (a dish's category, a promotion's branch). */
export function requestParentName(
  request: Pick<ChangeRequest, 'parent'>,
  fallback: string,
): string {
  return resolveEntityName(request.parent?.name, fallback);
}

/** Parent id when the parent is of the given `entity_type`, else `''`. */
export function requestParentId(
  request: Pick<ChangeRequest, 'parent'>,
  entityType: string,
): string {
  const parent = request.parent;
  if (!parent || (parent.entity_type ?? '').trim().toLowerCase() !== entityType) {
    return '';
  }
  return parent.entity_id?.trim() ?? '';
}

/**
 * Display name of a `v1ChangeRequestActor`: `first_name last_name` when
 * resolved, the raw system id (`system:…`) for system actors, else the
 * admin id shortened (names stay empty until auth resolves them).
 */
export function actorName(actor: ChangeRequest['reviewer']): string {
  if (!actor) return '—';
  const fullName = [actor.first_name?.trim(), actor.last_name?.trim()]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' ');
  if (fullName !== '') return fullName;
  const id = actor.id?.trim() ?? '';
  if (id === '') return '—';
  if ((actor.kind ?? '').trim().toLowerCase() === 'system') return id;
  return shortenId(id);
}

/** Who approved or rejected the request, for the verdict banners. */
export function requestReviewerName(request: Pick<ChangeRequest, 'reviewer'>): string {
  return actorName(request.reviewer);
}

/** Short human id (`93c4dd52…`) — mirrors `shortId` without the import cycle. */
function shortenId(id: string): string {
  const trimmed = id.trim();
  if (trimmed === '') return '—';
  return trimmed.length > 8 ? `${trimmed.slice(0, 8)}…` : trimmed;
}
